import React, { useState, useEffect } from 'react';
import JobDetailPanelSkeleton from './JobDetailPanelSkeleton';
import JobInquiryForm from './JobInquiryForm';
import { KIND_LABELS, skillKindIcon, groupSkillsByKind } from '../../constants/skillTaxonomy';

interface JobDetail {
  id: number;
  title: string;
  description: string;
  max_reward: number | null;
  min_reward: number | null;
  work_style: string;
  location_name: string;
  company_name?: string;
  skills?: Array<{ id: number; name: string; kind: string }>;
  occupations?: Array<{ id: number; name: string }>;
  industries?: Array<{ id: number; name: string }>;
  characteristics?: Array<{ id: number; name: string }>;
  published_at: string;
  created_at: string;
  updated_at: string;
  job_source?: { 
    id: number; 
    name: string; 
    display_name: string;
    official_name?: string;
    corporate_name?: string;
    logo_image?: string;
    thumbnail_image?: string;
  };
  workstyle_type_label?: string;
  contract_type?: { id: number; name: string };
  prefecture?: string;
  city?: string;
  station?: string;
  is_favorited?: boolean;
  required_skill?: string;
  preferred_skill?: string;
  development_environment?: string;
  work_hours?: string;
  settlement_hours?: string;
  interview_count?: number;
  payment_site?: string;
  recruiter_comment?: string;
  availability?: string;
  availability_type?: { key: string; name: string };
}

interface JobDetailPanelProps {
  jobDetail: JobDetail | null;
  onClose: () => void;
  currentUserId?: number;
  isUserSignedIn?: boolean;
  isProfileComplete?: boolean;
  isLoading?: boolean;
  totalJobListingsCount?: number;
}

const JobDetailPanel: React.FC<JobDetailPanelProps> = ({ 
  jobDetail, 
  onClose,
  currentUserId,
  isUserSignedIn = false,
  isProfileComplete = false,
  isLoading = false,
  totalJobListingsCount
}) => {
  const [isFavorited, setIsFavorited] = useState(jobDetail?.is_favorited || false);

  // jobDetailが変更された時にisFavorited状態を同期
  useEffect(() => {
    if (jobDetail) {
      setIsFavorited(jobDetail.is_favorited || false);
    }
  }, [jobDetail]);


  const handleConsultClick = () => {
    if (!isUserSignedIn) {
      // Not logged in -> apply signup page with job listing parameters
      const params = new URLSearchParams({
        job_listing_id: jobDetail?.id?.toString() || ''
      });
      window.location.href = `/apply/sign_up?${params.toString()}`;
    } else if (!isProfileComplete) {
      // Logged in but profile incomplete -> profile registration wizard
      window.location.href = '/registration/step1';
    } else {
      // Logged in with complete profile -> apply to job using POST
      // Create and submit a form for POST request
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `/job_listings/${jobDetail?.id}/apply`;
      
      // Add CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
      
      document.body.appendChild(form);
      form.submit();
    }
  };

  // ローディング状態の表示
  if (isLoading) {
    return <JobDetailPanelSkeleton />;
  }

  if (!jobDetail) {
    return (
    <div className="job-detail-panel">
      <div className="card h-100">
        <div className="card-body d-flex align-items-center justify-content-center" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{width: '100%', background: 'linear-gradient(135deg, #e8f8f5 0%, #d5f4e6 100%)', border: '3px solid #5dccb4', borderRadius: '8px', padding: '30px 20px', boxSizing: 'border-box'}}>
            
            {/* メインテキスト */}
            <h1 style={{color: '#3ab89d', textAlign: 'center', fontSize: '20px', fontWeight: 'bold', lineHeight: '1.5', margin: '0 0 25px 0'}}>
              インディバースフリーランスであなたにピッタリの<br/>フリーランス案件が見つかる
            </h1>
            
            {/* イラスト部分 */}
            <div style={{display: 'flex', justifyContent: 'center', gap: '30px', marginBottom: '30px', alignItems: 'center'}}>
              {/* 左の人物 */}
              <div style={{textAlign: 'center'}}>
                <img src="/assets/person-phone-1.png" alt="スマホを見る人" style={{width: '80px', height: 'auto', display: 'block'}} />
              </div>
              
              {/* 右の人物 */}
              <div style={{textAlign: 'center'}}>
                <img src="/assets/person-phone-2.png" alt="スマホを見る人" style={{width: '80px', height: 'auto', display: 'block'}} />
              </div>
            </div>
            
            {/* 説明文 */}
            <div style={{textAlign: 'center', marginBottom: '20px'}}>
              <p style={{fontSize: '16px', fontWeight: 'bold', color: '#333', margin: '0 0 10px 0'}}>
                {totalJobListingsCount && totalJobListingsCount > 0 ? `${(totalJobListingsCount / 10000).toFixed(0)}万件以上の` : '3万件以上の'}フリーランス案件から一括検索
              </p>
              <p style={{fontSize: '14px', color: '#666', margin: '0 0 15px 0', lineHeight: '1.4'}}>
                エンジニア・クリエイター・コンサルタント向けのたくさんの案件を一括検索可能です。
              </p>
              <p style={{fontSize: '12px', color: '#999', margin: '0 0 15px 0'}}>
                ※ {new Date().toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', weekday: 'short' })}更新<br/>
                {totalJobListingsCount && totalJobListingsCount > 0 ? `${(totalJobListingsCount / 10000).toFixed(0)}万件以上の` : '3万件以上の'}フリーランス案件から一括検索
              </p>
              <p style={{fontSize: '14px', color: '#666', margin: '0 0 20px 0', lineHeight: '1.4'}}>
                あなたの専門性と希望条件に最適な案件を厳選してお届け<br/>
                プロフィール登録で、あなただけのカスタマイズされた案件情報を受け取れます。時間をかけずに理想の案件を見つけましょう。
              </p>
            </div>
            
            {/* ボタン */}
            <div style={{textAlign: 'center'}}>
              <a href="https://freelance.indieverse.co.jp/sign_up" rel="nofollow" style={{display: 'inline-block', backgroundColor: '#3ab89d', color: 'white', fontSize: '16px', fontWeight: 'bold', padding: '15px 25px', border: 'none', borderRadius: '8px', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', fontFamily: 'inherit', textDecoration: 'none'}}>
                おすすめの案件を受け取る
              </a>
            </div>
            
          </div>
        </div>
      </div>
    </div>
    );
  }

  const formatSalary = (min: number | null, max: number | null) => {
    if (!min && !max) {
      return '単価要相談';
    }
    if (min && max && min === max) {
      return `${min.toLocaleString()}円/月`;
    }
    if (min && max) {
      return `${min.toLocaleString()}円〜${max.toLocaleString()}円/月`;
    }
    if (min) {
      return `${min.toLocaleString()}円〜/月`;
    }
    if (max) {
      return `〜${max.toLocaleString()}円/月`;
    }
    return '単価要相談';
  };

  const formatAvailability = (availability?: string, availabilityType?: { key: string; name: string }) => {
    // availability_typeが存在する場合はそれを優先
    if (availabilityType) {
      return availabilityType.name;
    }
    
    if (!availability) return '未設定';
    
    // 数字のみの場合は「週X日」形式に変換
    if (/^\d+$/.test(availability)) {
      return `週${availability}日`;
    }
    
    // 既に「週」が含まれている場合はそのまま
    if (availability.includes('週')) {
      return availability;
    }
    
    // その他の場合はそのまま表示
    return availability;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '日付不明';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '日付不明';
    
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1日前';
    if (diffDays <= 7) return `${diffDays}日前`;
    
    // 7日を超える場合は月単位で計算
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 0) return `${diffDays}日前`; // 1ヶ月未満だが7日超え
    if (diffMonths === 1) return '1ヶ月前';
    return `${diffMonths}ヶ月前`;
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      
      if (isFavorited) {
        // DELETE /favorites/:id
        const response = await fetch(`/favorites/${jobDetail.id}`, {
          method: 'DELETE',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        });
        
        if (response.ok) {
          setIsFavorited(false);
        } else {
          console.error('Failed to remove favorite:', response.status, response.statusText);
        }
      } else {
        // POST /favorites
        const response = await fetch('/favorites', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ job_listing_id: jobDetail.id })
        });
        
        if (response.ok) {
          setIsFavorited(true);
        } else {
          console.error('Failed to add favorite:', response.status, response.statusText);
        }
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  return (
    <div className="job-detail-panel">
      <div className="card h-100">
        <div className="card-body px-4 py-5" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* タイトル */}
          <div className="mb-3">
            <h3 className="card-title mb-0 fw-bold" style={{ 
              fontSize: '1.5rem', 
              lineHeight: '1.6',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}>
              {jobDetail.title}
            </h3>
          </div>

          {/* 特徴タグ */}
          {(() => {
            const characteristics = Array.isArray(jobDetail.characteristics) ? jobDetail.characteristics : [];
            return characteristics.length > 0 && (
              <div className="mb-3">
                <div className="d-flex flex-wrap gap-1">
                  {characteristics.slice(0, 3).map((characteristic, index) => (
                    <a
                      key={index}
                      href={`/characteristics/${characteristic?.id || '#'}`}
                      className="badge bg-primary text-decoration-none"
                    >
                      {characteristic?.name || 'Unknown'}
                    </a>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* 基本情報サマリー */}
          <hr className="my-4" />
          <div className="mb-4">
            <div className="row">
              <div className="col-md-3 mb-2">
                <div className="d-flex align-items-center">
                  <i className="fas fa-yen-sign me-2 text-muted" style={{ width: '16px', textAlign: 'center' }}></i>
                  <div>
                    <div className="fw-bold text-danger" style={{ fontSize: '1.1rem' }}>
                      {formatSalary(jobDetail.min_reward, jobDetail.max_reward)}
                    </div>
                  </div>
                </div>
              </div>
              {jobDetail.contract_type?.name && (
                <div className="col-md-3 mb-2">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-briefcase me-2 text-muted" style={{ width: '16px', textAlign: 'center' }}></i>
                    <div>
                      <div>{jobDetail.contract_type.name}</div>
                    </div>
                  </div>
                </div>
              )}
              {jobDetail.location_name && jobDetail.location_name !== '未設定' && (
                <div className="col-md-3 mb-2">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-map-marker-alt me-2 text-muted" style={{ width: '16px', textAlign: 'center' }}></i>
                    <div>
                      <div>{jobDetail.location_name}</div>
                    </div>
                  </div>
                </div>
              )}
              {jobDetail.workstyle_type_label && jobDetail.workstyle_type_label !== '未設定' && (
                <div className="col-md-3 mb-2">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-laptop me-2 text-muted" style={{ width: '16px', textAlign: 'center' }}></i>
                    <div>
                      <div>{jobDetail.workstyle_type_label}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <hr className="my-4" />

          {/* アクションボタン */}
          <div className="my-5">
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                style={{ 
                  color: isFavorited ? '#dc3545' : '#6c757d',
                  borderColor: isFavorited ? '#dc3545' : '#6c757d',
                  minWidth: 'auto',
                  flexShrink: 1
                }}
                onClick={handleFavoriteClick}
              >
                <i className={isFavorited ? 'fas fa-bookmark' : 'far fa-bookmark'}></i>
                <span className="d-none d-sm-inline ms-1">{isFavorited ? '保存済み' : '保存する'}</span>
              </button>
              <button 
                className="btn btn-primary"
                style={{ flex: '1 1 0%', minWidth: 0 }}
                onClick={handleConsultClick}
              >
                {isUserSignedIn && isProfileComplete ? '応募する' : 'この案件について相談する'}
              </button>
            </div>
          </div>

          {/* 職種・ポジション */}
          {(() => {
            const occupations = Array.isArray(jobDetail.occupations) ? jobDetail.occupations : [];
            return occupations.length > 0 && (
              <div className="row mb-2">
                <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">職種・ポジション</div>
                <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                  <div className="d-flex flex-wrap gap-1">
                    {occupations.map((occupation, index) => (
                      <a
                        key={index}
                        href={`/occupations/${occupation?.id || '#'}`}
                        className="badge bg-primary text-decoration-none"
                      >
                        {occupation?.name || 'Unknown'}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 業界 */}
          {(() => {
            const industries = Array.isArray(jobDetail.industries) ? jobDetail.industries : [];
            return industries.length > 0 && (
              <div className="row mb-2">
                <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">業界</div>
                <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                  <div className="d-flex flex-wrap gap-1">
                    {industries.map((industry, index) => (
                      <a
                        key={index}
                        href={`/industries/${industry?.id || '#'}`}
                        className="badge bg-primary text-decoration-none"
                      >
                        {industry?.name || 'Unknown'}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 作業内容 */}
          <div className="row mb-2">
            <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">作業内容</div>
            <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
              <p className="mb-0" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                {jobDetail.description}
              </p>
            </div>
          </div>

          {/* 開発環境 */}
          {(() => {
            const skills = Array.isArray(jobDetail.skills) ? jobDetail.skills : [];
            const skillsGroupedByKind = groupSkillsByKind(skills);
            const kindKeys = Object.keys(skillsGroupedByKind);
            
            return kindKeys.length > 0 && (
              <div className="row mb-2">
                <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">開発環境</div>
                <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                  {kindKeys.map((kind) => {
                    const kindSkills = skillsGroupedByKind[kind];
                    return (
                      <div key={kind} className="mb-3 d-flex align-items-center flex-wrap gap-2">
                        <div className="fw-bold text-dark d-flex align-items-center" style={{ fontSize: '0.9rem', minWidth: 'fit-content' }}>
                          <i className={`${skillKindIcon(kind)} me-1`}></i>
                          {KIND_LABELS[kind] || kind}:
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {kindSkills.map((skill) => (
                            <a
                              key={skill.id}
                              href={`/skills/${skill.id}`}
                              className="badge bg-primary text-decoration-none"
                            >
                              {skill.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* 必須スキル */}
          {jobDetail.required_skill && (
            <div className="row mb-2">
              <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">必須スキル</div>
              <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                <p className="mb-0" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                  {jobDetail.required_skill}
                </p>
              </div>
            </div>
          )}

          {/* 歓迎スキル */}
          {jobDetail.preferred_skill && (
            <div className="row mb-2">
              <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">歓迎スキル</div>
              <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                <p className="mb-0" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                  {jobDetail.preferred_skill}
                </p>
              </div>
            </div>
          )}

          {/* 稼働日数 */}
          {(jobDetail.availability || jobDetail.availability_type) && (
            <div className="row mb-2">
              <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">稼働日数</div>
              <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                <p className="mb-0">{formatAvailability(jobDetail.availability, jobDetail.availability_type)}</p>
              </div>
            </div>
          )}

          {/* 勤務形態 */}
          {jobDetail.workstyle_type_label && (
            <div className="row mb-2">
              <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">勤務形態</div>
              <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                <p className="mb-0">{jobDetail.workstyle_type_label}</p>
              </div>
            </div>
          )}

          {/* 勤務地 */}
          {(jobDetail.location_name || jobDetail.prefecture || jobDetail.city || jobDetail.station) && (
            <div className="row mb-2">
              <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">勤務地</div>
              <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                <p className="mb-0">
                  {[jobDetail.location_name, jobDetail.prefecture, jobDetail.city, jobDetail.station]
                    .filter(Boolean)
                    .join(' ')}
                </p>
              </div>
            </div>
          )}

          {/* 面談回数 */}
          {jobDetail.interview_count && (
            <div className="row mb-2">
              <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">面談回数</div>
              <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                <p className="mb-0">{jobDetail.interview_count}回</p>
              </div>
            </div>
          )}

          {/* 精算基準時間 */}
          {jobDetail.work_hours && (
            <div className="row mb-2">
              <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">精算基準時間</div>
              <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                <p className="mb-0">{jobDetail.work_hours}</p>
              </div>
            </div>
          )}

          {/* 作業時間 */}
          {jobDetail.development_environment && (
            <div className="row mb-2">
              <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">作業時間</div>
              <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                <p className="mb-0">{jobDetail.development_environment}</p>
              </div>
            </div>
          )}

          {/* 募集回数 */}
          <div className="row mb-2">
            <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">募集回数</div>
            <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
              <p className="mb-0">1回</p>
            </div>
          </div>

          {/* 最終更新日 */}
          <div className="row mb-2">
            <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">最終更新日</div>
            <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
              <p className="mb-0">{formatDate(jobDetail.updated_at || jobDetail.created_at)}</p>
            </div>
          </div>

          {/* 募集背景 */}
          {jobDetail.recruiter_comment && (
            <div className="row mb-2">
              <div className="col-md-3 bg-light py-3 px-4 text-muted fw-bold">募集背景</div>
              <div className="col-md-9 py-3 px-4" style={{ color: '#9ca3af' }}>
                <p className="mb-0" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                  {jobDetail.recruiter_comment}
                </p>
              </div>
            </div>
          )}

          {/* Application CTA Section */}
          <div className="mb-4 mt-4">
            <div className="alert alert-warning" role="alert">
              <div className="text-center">
                <div className="mb-2">3件以上の応募でご希望の条件の案件に参画しやすくなります</div>
                <div>人気案件は申し込みが集中します。ご相談はお早めに！</div>
              </div>
            </div>
            
            <div className="d-flex justify-content-center gap-3 mt-3">
              <button
                className="btn btn-outline-secondary"
                style={{ 
                  color: isFavorited ? '#dc3545' : '#6c757d',
                  borderColor: isFavorited ? '#dc3545' : '#6c757d'
                }}
                onClick={handleFavoriteClick}
              >
                <i className={`${isFavorited ? 'fas' : 'far'} fa-bookmark me-2`}></i>
                {isFavorited ? '保存済み' : '保存する'}
              </button>
              
              <button
                className="btn btn-primary"
                onClick={handleConsultClick}
              >
                {isUserSignedIn && isProfileComplete ? '応募する' : 'この案件について相談する'}
              </button>
            </div>
          </div>

          {/* エージェント情報 */}
          {jobDetail.job_source && (
            <div className="mb-4 mt-5">
              <hr className="mb-4" />
              <h5 className="mb-3 fw-bold">この案件を掲載しているエージェントについて</h5>
              <div className="card">
                <div className="card-body">
                <div className="d-flex align-items-center mb-2">
                  <div className="me-3">
                    <a 
                      href={`/job_sources/${jobDetail.job_source.id}`}
                      className="text-decoration-none"
                      style={{ display: 'block' }}
                    >
                      {jobDetail.job_source.logo_image ? (
                        <img 
                          src={jobDetail.job_source.logo_image} 
                          alt={jobDetail.job_source.name}
                          style={{ width: '60px', height: '40px', objectFit: 'contain' }}
                          className="rounded"
                        />
                      ) : (
                        <div className="bg-secondary rounded d-flex align-items-center justify-content-center" style={{ width: '60px', height: '40px' }}>
                          <i className="fas fa-building text-white"></i>
                        </div>
                      )}
                    </a>
                  </div>
                  <div>
                    <h6 className="mb-1">
                      <a 
                        href={`/job_sources/${jobDetail.job_source.id}`}
                        className="text-decoration-none"
                        style={{ color: '#00bb94' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#00cc9f'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#00bb94'}
                      >
                        {jobDetail.job_source.display_name}
                      </a>
                    </h6>
                    <small className="text-muted">運営会社：{jobDetail.job_source.corporate_name || jobDetail.job_source.name}</small>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <small className="text-muted">支払いサイト：</small>
                    <div>{jobDetail.payment_site || '非公開'}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted">契約形態：</small>
                    <div>{jobDetail.contract_type?.name || '業務委託(フリーランス)'}</div>
                  </div>
                </div>
                <div className="mt-3">
                  <a 
                    href={`/job_sources/${jobDetail.job_source.id}`}
                    className="btn btn-primary w-100"
                  >
                    {jobDetail.job_source.official_name || jobDetail.job_source.display_name} の詳細を見る
                  </a>
                </div>
                </div>
              </div>
            </div>
          )}

          {/* Job Inquiry Form - Only show for guest users */}
          {!isUserSignedIn && (
            <JobInquiryForm jobListingId={jobDetail.id} />
          )}
        </div>

      </div>
    </div>
  );
};

export default JobDetailPanel;
